import { isBrowser, isServiceWorker } from '../device-type';

export let appShellTemplateUrl = '/appshell/template';
const requestTimeout = 200;

export const hasServiceWorker = isBrowser() && !isServiceWorker() && WITH_SERVICEWORKER && 'serviceWorker' in navigator;

export function clearCache(explicitUrlsToClear: string[] = []): Promise<boolean> {
  if (!self.caches) {
    return Promise.resolve(true);
  }

  // It might seem odd that `clearCache` only clears the app shell template
  // but the app shell template is the only entry in the cache that is mutable.
  // If any of the other entries change they will get new urls and a new app shell template
  // will point to the new resources instead.
  // Old entries will eventually get cleaned up by the logic in `onInstall`.
  explicitUrlsToClear.push(appShellTemplateUrl);

  return cacheKeys()
    .then((cacheNames: string[]) => Promise.all(cacheNames.map(cacheName => openCache(cacheName))))
    .then((caches: Array<Cache | undefined>) => {
      return Promise.all(
        caches.map(cache => Promise.all(explicitUrlsToClear.map(url => deleteFromCache(cache, new Request(url))))),
      );
    })
    .then(cacheDeletes => cacheDeletes.every(deletes => deletes.every(d => d)));
}

// The reason for having this is because it seems that some browsers hang when trying to respond
// to requests to the `caches` object.
// IndexedDB has been riddled with dead locks for years (see https://github.com/localForage/localForage/pull/826)
// and we already have a timeout race for when we explicitly use IndexedDB. But it seems as if the Service Worker
// cache is affected as well. It might be that some browsers implement the SW caches on top of IndexedDB.
function raceWithTimeout<T>(cacheAccessor: () => Promise<T>, resolveWithForTimeout: T): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let resolvedOrRejected = false;
    const timer = setTimeout(() => {
      if (!resolvedOrRejected) {
        resolvedOrRejected = true;
        if (self && self.console && self.console.warn) {
          // tslint:disable:no-string-literal
          self['console']['warn'](
            "Call to SW caches timed out, skipping cache and continuing as if the cache didn't exist/was empty",
          );
          // tslint:enable:no-string-literal
        }
        resolve(resolveWithForTimeout);
      }
    }, requestTimeout);

    cacheAccessor().then(
      r => {
        if (!resolvedOrRejected) {
          resolvedOrRejected = true;
          clearTimeout(timer);
          resolve(r);
        }
      },
      e => {
        if (!resolvedOrRejected) {
          resolvedOrRejected = true;
          clearTimeout(timer);
          reject(e);
        }
      },
    );
  });
}

export function cacheKeys(): Promise<string[]> {
  return raceWithTimeout(() => caches.keys(), []);
}

export function openCache(name: string): Promise<Cache | undefined> {
  return raceWithTimeout(() => caches.open(name), undefined);
}

export function cacheMatch(cache: Cache | undefined, request: Request): Promise<Response | undefined> {
  if (!cache) {
    return Promise.resolve(undefined);
  }
  return raceWithTimeout(() => cache.match(request), undefined);
}

export function deleteCache(name: string): Promise<boolean> {
  return raceWithTimeout(() => caches.delete(name), false);
}

export function deleteFromCache(cache: Cache | undefined, request: Request): Promise<boolean> {
  if (!cache) {
    return Promise.resolve(false);
  }
  return raceWithTimeout(() => cache.delete(request), false);
}
